import { Maximize, Minimize } from 'lucide-react'
import { useEffect, useState } from 'react'

/** How long the control stays after the pointer last moved. */
const IDLE_MS = 4000

/**
 * The way a screen takes the whole display, browser chrome and all.
 *
 * A page may only go full screen from a press, so the board offers one: a
 * control that shows on load and whenever the pointer moves or a key is
 * pressed, and fades once nobody is touching it. F does the same from a
 * keyboard or a remote. It is absent where the browser cannot go full screen
 * and inside a frame — the editor's preview, which offers its own.
 */
export function FullScreen() {
  const [full, setFull] = useState(() => !!document.fullscreenElement)
  const [awake, setAwake] = useState(true)
  const able = document.fullscreenEnabled && window.self === window.top

  // The pointer hides with the control, so a mouse can still find it.
  useEffect(() => {
    const root = document.documentElement
    if (awake) delete root.dataset.idle
    else root.dataset.idle = ''
    return () => {
      delete root.dataset.idle
    }
  }, [awake])

  useEffect(() => {
    if (!able) return
    let timer = window.setTimeout(() => setAwake(false), IDLE_MS)
    const wake = () => {
      setAwake(true)
      window.clearTimeout(timer)
      timer = window.setTimeout(() => setAwake(false), IDLE_MS)
    }
    const onKey = (e: KeyboardEvent) => {
      wake()
      if (e.key === 'f' || e.key === 'F') toggle()
    }
    const onChange = () => setFull(!!document.fullscreenElement)
    window.addEventListener('pointermove', wake)
    window.addEventListener('keydown', onKey)
    document.addEventListener('fullscreenchange', onChange)
    return () => {
      window.clearTimeout(timer)
      window.removeEventListener('pointermove', wake)
      window.removeEventListener('keydown', onKey)
      document.removeEventListener('fullscreenchange', onChange)
    }
  }, [able])

  if (!able) return null
  const Icon = full ? Minimize : Maximize
  return (
    <button
      type="button"
      className="display-board__full"
      data-open={awake ? '' : undefined}
      onClick={toggle}
    >
      <Icon aria-hidden size="1em" />
      {full ? 'Leave full screen' : 'Full screen'}
    </button>
  )
}

function toggle() {
  if (document.fullscreenElement) void document.exitFullscreen()
  else void document.documentElement.requestFullscreen()
}
